// ChatMentionAutocomplete.jsx — typeahead pra `#chat:` (contexto de CONVERSA
// passada, recuperação Aba-Apolo C2). Espelha o FileMentionAutocomplete (@file:)
// mas pro prefixo `#`. Lista as sessões de CHAT do store (isChatSession), exclui
// a sessão atual, e ao escolher insere `#chat:<id> ` — resolvido no envio por
// mentions.js (resolveAndEnrich → io.loadChat). Teclado em CAPTURE. FLAT.

import { useEffect, useMemo, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { useAgentStore } from '../../store/useAgentStore.js';
import { isChatSession } from '../../lib/code/sessionSurface.js';

// Detecta um `#chat:<partial>` (ou um `#` sendo digitado) NO FIM do texto,
// precedido por início/espaço — não dispara em `# heading` (tem espaço depois)
// nem no meio de uma palavra.
export function detectChatMention(input) {
  const m = /(?:^|\s)#(?:chat:)?([^\s#@<>'"]*)$/.exec(input || '');
  if (!m) return null;
  const lead = m[0].startsWith('#') ? 0 : 1; // pula o espaço líder, se houver
  return { partial: m[1] || '', start: m.index + lead, end: (input || '').length };
}

/** Substitui o span `#chat:<partial>` detectado por `#chat:<id> `. */
export function applyChatMention(text, detected, session) {
  if (!detected || !session?.id) return text;
  const before = text.slice(0, detected.start);
  const after = text.slice(detected.end);
  return `${before}#chat:${session.id} ${after}`;
}

export default function ChatMentionAutocomplete({ input, currentSessionId, onPick, onDismiss }) {
  const sessions = useAgentStore((s) => s.code.sessions);
  const detected = detectChatMention(input);
  const q = (detected?.partial || '').trim().toLowerCase();

  const matches = useMemo(() => {
    if (!detected) return [];
    return (sessions || [])
      .filter(isChatSession)
      .filter((s) => s.id !== currentSessionId)
      .filter((s) => !q || (s.name || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [detected, q, sessions, currentSessionId]);

  const [activeIdx, setActiveIdx] = useState(0);
  const visible = detected !== null && matches.length > 0;

  useEffect(() => {
    setActiveIdx(0);
  }, [q, matches.length]);

  useEffect(() => {
    if (!visible) return undefined;
    function onKey(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIdx((i) => (i + 1) % matches.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setActiveIdx((i) => (i - 1 + matches.length) % matches.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        const s = matches[activeIdx];
        if (s) onPick?.(detected, s);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onDismiss?.();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [visible, matches, activeIdx, detected, onPick, onDismiss]);

  if (!visible) return null;

  return (
    <div
      role="listbox"
      aria-label="Conversas passadas"
      className="absolute bottom-full left-0 z-40 mb-1 max-h-56 w-72 overflow-y-auto rounded-lg border border-apolo-borderStrong bg-apolo-surface2 p-1 shadow-lg"
    >
      <div className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-apolo-textTertiary">
        Contexto · conversas passadas
      </div>
      {matches.map((s, i) => (
        <button
          key={s.id}
          type="button"
          role="option"
          aria-selected={i === activeIdx}
          onMouseEnter={() => setActiveIdx(i)}
          onClick={() => onPick?.(detected, s)}
          className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition ${
            i === activeIdx ? 'bg-apolo-surface3' : 'hover:bg-apolo-surface3/60'
          }`}
        >
          <MessageSquare size={13} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />
          <span className="min-w-0 flex-1 truncate text-[12px] text-apolo-textPrimary">{s.name || 'Sessão'}</span>
        </button>
      ))}
    </div>
  );
}
